import React from 'react';
import { Check } from 'lucide-react';

/**
 * Shared list used by the journey pop-ups and login status screens:
 * "Left to do", "What happens next", "Needs changes", etc.
 *
 * Row kinds:
 *  now      – the next thing to do (filled purple dot)
 *  todo     – still to do (empty circle)
 *  done     – complete (green check)
 *  stepnow  – current numbered step (purple)
 *  step     – later numbered step (outlined)
 *  dot      – plain bullet
 *  flag     – needs attention (amber "!")
 */
const Marker = ({ kind, index }) => {
    switch (kind) {
        case 'now':
            return <span className="h-[18px] w-[18px] shrink-0 rounded-full bg-secondary" />;
        case 'done':
            return (
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-success text-white">
                    <Check className="h-3 w-3" strokeWidth={3.5} />
                </span>
            );
        case 'stepnow':
            return (
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-white">
                    {index}
                </span>
            );
        case 'step':
            return (
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold text-gray-400">
                    {index}
                </span>
            );
        case 'dot':
            return <span className="mx-[5px] h-2 w-2 shrink-0 rounded-full bg-gray-300" />;
        case 'flag':
            return (
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-amber-400 text-[11px] font-bold text-white">
                    !
                </span>
            );
        default:
            return <span className="h-[18px] w-[18px] shrink-0 rounded-full border-2 border-gray-300" />;
    }
};

/**
 * @param {string} title   Uppercase label above the list
 * @param {string} meta    Optional text on the right of the label
 * @param {Array}  rows    [{ kind, label, meta, detail, badge }]
 */
const JourneyList = ({ title, meta, rows = [] }) => {
    if (!rows.length) return null;
    let stepNumber = 0;

    return (
        <div className="text-left">
            {(title || meta) && (
                <div className="flex items-baseline justify-between gap-3">
                    <p className="m-0 text-[11px] font-bold uppercase tracking-wider text-gray-500">{title}</p>
                    {meta && <span className="text-[11px] md:text-xs font-semibold text-gray-500 whitespace-nowrap">{meta}</span>}
                </div>
            )}
            <ul className="m-0 mt-1 p-0 list-none divide-y divide-gray-100">
                {rows.map((row, i) => {
                    const isStep = row.kind === 'step' || row.kind === 'stepnow';
                    if (isStep) stepNumber += 1;
                    const active = row.kind === 'now' || row.kind === 'stepnow';

                    return (
                        <li key={`${row.label}-${i}`} className="flex items-center gap-3 py-2.5 md:py-3">
                            <Marker kind={row.kind} index={stepNumber} />
                            <div className="grow shrink basis-0 min-w-0">
                                <p className={TB.mapClass('m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm md:text-[15px] leading-5', {
                                    'font-semibold text-secondary': active,
                                    'text-gray-900': !active,
                                })}>
                                    {row.label}
                                    {row.badge && (
                                        <span className="rounded-full bg-secondary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-secondary">
                                            {row.badge}
                                        </span>
                                    )}
                                </p>
                                {row.detail && (
                                    <p className="m-0 mt-0.5 text-xs md:text-[13px] text-gray-500 whitespace-pre-line">{row.detail}</p>
                                )}
                            </div>
                            {row.meta && (
                                <span className={TB.mapClass('shrink-0 text-xs md:text-[13px] whitespace-nowrap tabular-nums', {
                                    'font-bold text-secondary': active,
                                    'text-gray-400': !active,
                                })}>
                                    {row.meta}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

/** Green "all done" confirmation strip. */
export const OkBox = ({ children }) => (
    <div className="flex items-center gap-2 rounded-lg bg-success/10 px-3 py-2.5 text-[13px] md:text-sm font-semibold text-green-700">
        <Check className="h-4 w-4 shrink-0" strokeWidth={3} /> {children}
    </div>
);

/** Centered primary/secondary actions (stacks on phones). */
export const JourneyActions = ({ children }) => (
    <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-3">
        {children}
    </div>
);

/** Small centered footnote under the actions. */
export const JourneyFoot = ({ children }) => (
    <p className="m-0 text-center text-xs md:text-[13px] text-gray-500">{children}</p>
);

/**
 * Turn remaining sections into "Left to do" rows, highlighting the first.
 * Sections come from getJourneyProgress()/getOptionalSuggestions().
 */
export const sectionRows = (sections = [], { badgeFirst } = {}) => sections.map((section, i) => ({
    kind: i === 0 ? 'now' : 'todo',
    label: section.title,
    meta: section.needsConfirm ? 'Confirm' : `~${section.minutes} min`,
    badge: i === 0 ? badgeFirst : undefined,
}));

export default JourneyList;
